import React, { useState, useEffect } from 'react';
import { useT } from './BuilderApp.jsx';
import { CheckIcon } from '../components/ui/Icons.jsx';

export default function BuildingScreen({ config, onDone, onError }) {
  const t = useT();
  const [current, setCurrent] = useState(0);
  const [apiResult, setApiResult] = useState(null);
  const [apiDone, setApiDone] = useState(false);
  const steps = t.build_steps;
  const animDone = current >= steps.length;

  // Mock API (no fetch)
  useEffect(() => {
    const slug = config.slug ||
      (config.custom_domain
        ? config.custom_domain.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-')
        : 'site-' + Math.random().toString(36).slice(2, 8));

    const timer = setTimeout(() => {
      setApiResult({ live_url: `https://${slug}.chamcore.site`, site_id: 'demo-' + slug });
      setApiDone(true);
    }, 1800);
    return () => clearTimeout(timer);
  }, []);

  // Animation ticker
  useEffect(() => {
    if (current >= steps.length) return;
    const timer = setTimeout(() => setCurrent(c => c + 1), 800 + Math.random() * 600);
    return () => clearTimeout(timer);
  }, [current, steps.length]);

  // Complete only when both animation and API are done
  useEffect(() => {
    if (animDone && apiDone && apiResult) onDone(apiResult);
  }, [animDone, apiDone, apiResult]);

  const pct = Math.round((current / steps.length) * 100);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem', position: 'relative', zIndex: 1, textAlign: 'center' }}>
      <div style={{ width: 72, height: 72, borderRadius: 20, marginBottom: '2.25rem', background: 'linear-gradient(135deg,#2563eb,#7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.9rem', animation: 'pulse 1.8s ease-in-out infinite' }}>⚡</div>
      <h2 style={{ fontSize: '1.75rem', fontWeight: 800, letterSpacing: '-.02em', marginBottom: '.5rem' }}>{t.build_h}</h2>
      <p style={{ color: 'var(--muted)', marginBottom: '2.25rem', fontSize: '.9rem' }}>{t.build_p}</p>

      <div style={{ width: '100%', maxWidth: 380, marginBottom: '2rem' }}>
        <div className="progress-bar" style={{ height: 4, marginBottom: '.65rem' }}>
          <div className="progress-fill" style={{ width: `${pct}%` }}/>
        </div>
        <div style={{ fontSize: '.77rem', color: 'var(--muted)' }}>{t.build_pct(pct)}</div>
      </div>

      <div style={{ width: '100%', maxWidth: 360, display: 'flex', flexDirection: 'column', gap: '.45rem', textAlign: 'left' }}>
        {steps.map((s, i) => {
          const done = i < current;
          const active = i === current;
          return (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: '.7rem',
              padding: '.55rem .85rem', borderRadius: 10,
              background: active ? 'rgba(59,130,246,.1)' : 'transparent',
              border: active ? '1px solid rgba(59,130,246,.2)' : '1px solid transparent',
              opacity: done ? .45 : active ? 1 : .25,
              transition: 'all .3s',
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: done ? 'rgba(52,211,153,.15)' : active ? 'rgba(59,130,246,.15)' : 'rgba(255,255,255,.05)',
                border: done ? '1px solid rgba(52,211,153,.3)' : active ? '1px solid rgba(59,130,246,.3)' : '1px solid var(--border)',
                fontSize: '.83rem',
              }}>
                {done ? <CheckIcon size={12}/> : s.icon}
              </div>
              <span style={{ fontSize: '.82rem', fontWeight: active ? 700 : 500, color: active ? 'var(--text)' : 'var(--muted)', flex: 1 }}>{s.label}</span>
              {active && (
                <svg style={{ animation: 'spin 1s linear infinite', flexShrink: 0 }} width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <circle cx="7" cy="7" r="5.5" stroke="rgba(255,255,255,.15)" strokeWidth="1.5"/>
                  <path d="M7 1.5a5.5 5.5 0 015.5 5.5" stroke="var(--blue2)" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
